import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from 'react-beautiful-dnd';
import { type DynamicChartConfig, CHART_REGISTRY } from './ChartRegistry';

export type WidgetDef = {
  id: string;
  label: string;
  icon: string;
  desc: string;
  defaultW: number;
  adminOnly?: boolean;
  nonAdminOnly?: boolean;
};

export type CustomChartType = 'donut' | 'bar-v' | 'bar-h' | 'table';
export type CustomDataSource = 'task-status' | 'per-standard' | 'workload' | 'actions';
export type CustomChart = {
  id: string;
  label: string;
  dataSource: CustomDataSource;
  chartType: CustomChartType;
  width: number;
};

interface Props {
  widgetDefs: readonly WidgetDef[];
  widgetOrder: string[];
  widgetWidths: Record<string, number>;
  hiddenWidgets: Set<string>;
  customCharts: CustomChart[];
  dynamicCharts: DynamicChartConfig[];
  isAdmin: boolean;
  onReorder: (newOrder: string[]) => void;
  onWidthChange: (id: string, w: number) => void;
  onToggleHide: (id: string) => void;
  onAddCustom: (c: CustomChart) => void;
  onRemoveCustom: (id: string) => void;
  onRemoveDynamic: (id: string) => void;
  onOpenDynamicBuilder: () => void;
  onReset: () => void;
  onClose: () => void;
}

const WIDTH_OPTIONS = [
  { label: '1/4', value: 3 },
  { label: '1/3', value: 4 },
  { label: '1/2', value: 6 },
  { label: '2/3', value: 8 },
  { label: 'Full', value: 12 },
];

const DATA_SOURCES: { value: CustomDataSource; label: string; icon: string }[] = [
  { value: 'task-status',   label: 'Task Status',            icon: '🍩' },
  { value: 'per-standard',  label: 'Compliance by Standard', icon: '📋' },
  { value: 'workload',      label: 'Team Workload',           icon: '👥' },
  { value: 'actions',       label: 'Compliance Actions',      icon: '⚡' },
];

const CHART_TYPES: { value: CustomChartType; label: string; icon: string }[] = [
  { value: 'donut',  label: 'Donut Chart',     icon: '🍩' },
  { value: 'bar-v',  label: 'Vertical Bar',    icon: '📊' },
  { value: 'bar-h',  label: 'Horizontal Bar',  icon: '📉' },
  { value: 'table',  label: 'Data Table',      icon: '🗃️' },
];

const ACCENT = '#5a9b82';
const PANEL_W = 360;

const widthBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '2px 7px', borderRadius: 5, fontSize: 11, fontWeight: active ? 700 : 400,
  border: `1px solid ${active ? ACCENT : '#E5E7EB'}`,
  background: active ? ACCENT : '#fff', color: active ? '#fff' : '#6B7280',
  cursor: 'pointer', transition: 'all 0.15s',
});

const toggleBtnStyle = (hidden: boolean): React.CSSProperties => ({
  padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
  border: `1px solid ${hidden ? '#E5E7EB' : ACCENT}`,
  background: hidden ? '#F3F4F6' : ACCENT + '18',
  color: hidden ? '#9CA3AF' : ACCENT, cursor: 'pointer', whiteSpace: 'nowrap',
});

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1040,
    display: 'flex', justifyContent: 'flex-end',
  },
  panel: {
    width: PANEL_W, background: '#fff', height: '100%', overflowY: 'auto',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column',
    animation: 'slideInRight 0.22s ease',
  },
  header: {
    padding: '16px 16px 12px', borderBottom: '1px solid #E5E7EB',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#F9FAFB', position: 'sticky', top: 0, zIndex: 2,
  },
  section: { padding: '12px 16px', borderBottom: '1px solid #F3F4F6' },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 },
  widgetRow: {
    display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
    borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', marginBottom: 6,
  },
  dragHandle: { color: '#D1D5DB', cursor: 'grab', fontSize: 16, paddingTop: 2, flexShrink: 0 },
  widgetLabel: { fontSize: 13, fontWeight: 600, color: '#374151', lineHeight: '20px' },
  widgetDesc: { fontSize: 11, color: '#9CA3AF', marginTop: 1 },
  addBtn: {
    padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
    background: '#F0FDF4', border: `1px solid ${ACCENT}`, color: ACCENT, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center',
    marginBottom: 6,
  },
  input: {
    width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #E5E7EB',
    fontSize: 13, color: '#374151', outline: 'none', marginBottom: 8,
  },
  select: {
    width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #E5E7EB',
    fontSize: 13, color: '#374151', background: '#fff', marginBottom: 8, cursor: 'pointer',
  },
  createBtn: {
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: ACCENT, color: '#fff', border: 'none', cursor: 'pointer', width: '100%',
  },
  removeBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
    fontSize: 14, padding: '2px 4px', lineHeight: 1, flexShrink: 0,
  },
};

export default function DashboardCustomizer({
  widgetDefs, widgetOrder, widgetWidths, hiddenWidgets,
  customCharts, dynamicCharts, isAdmin, onReorder, onWidthChange,
  onToggleHide, onAddCustom, onRemoveCustom, onRemoveDynamic,
  onOpenDynamicBuilder, onReset, onClose,
}: Props) {
  const [newLabel, setNewLabel]         = useState('');
  const [newSource, setNewSource]       = useState<CustomDataSource>('task-status');
  const [newType, setNewType]           = useState<CustomChartType>('bar-v');
  const [newWidth, setNewWidth]         = useState<number>(6);
  const [showBuilder, setShowBuilder]   = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Visible + hidden split (exclude role-filtered)
  const applicable = widgetDefs.filter(w =>
    (!w.adminOnly || isAdmin) && (!w.nonAdminOnly || !isAdmin)
  );
  const hiddenIds   = widgetOrder.filter(id => hiddenWidgets.has(id));

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = [...widgetOrder];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onReorder(reordered);
  };

  const handleAddCustom = () => {
    if (!newLabel.trim()) return;
    const id = `custom-${Date.now()}`;
    onAddCustom({ id, label: newLabel.trim(), dataSource: newSource, chartType: newType, width: newWidth });
    setNewLabel('');
    setNewSource('task-status');
    setNewType('bar-v');
    setNewWidth(6);
    setShowBuilder(false);
  };

  const getW = (id: string, def: number) => widgetWidths[id] ?? def;

  const renderWidgetRow = (id: string, label: string, icon: string, desc: string, defaultW: number, dragIndex: number, isDragging: boolean, dragHandleProps: any, onRemove?: () => void) => (
    <div style={{ ...styles.widgetRow, boxShadow: isDragging ? '0 4px 16px rgba(0,0,0,0.12)' : 'none', background: isDragging ? '#F0FDF4' : '#fff' }}>
      <span style={styles.dragHandle} {...dragHandleProps}>⠿</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.widgetLabel}>{icon} {label}</div>
        <div style={styles.widgetDesc}>{desc}</div>
        {/* Width selector */}
        <div style={{ display: 'flex', gap: 3, marginTop: 5, flexWrap: 'wrap' }}>
          {WIDTH_OPTIONS.map(o => (
            <button key={o.value} style={widthBtnStyle(getW(id, defaultW) === o.value)}
              onClick={() => onWidthChange(id, o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
        <button style={toggleBtnStyle(hiddenWidgets.has(id))} onClick={() => onToggleHide(id)}>
          {hiddenWidgets.has(id) ? '+ Show' : '✓ Visible'}
        </button>
        {onRemove && (
          <button style={styles.removeBtn} title="Remove" onClick={onRemove}>✕</button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(${PANEL_W}px); opacity: 0; }
          to   { transform: translateX(0);             opacity: 1; }
        }
      `}</style>
      <div style={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={styles.panel}>
          {/* Header */}
          <div style={styles.header}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>⚙ Customize Dashboard</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Drag to reorder · toggle visibility · adjust width</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: 20, lineHeight: 1, padding: 4 }}>✕</button>
          </div>

          {/* Widget list */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Layout (drag to reorder)</div>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="widgets">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {widgetOrder.map((id, idx) => {
                      const def = applicable.find(w => w.id === id);
                      const custom = customCharts.find(c => c.id === id);
                      const dyn = dynamicCharts.find(d => d.id === id);
                      if (!def && !custom && !dyn) return null;
                      const dynMod   = dyn ? CHART_REGISTRY.find(m => m.id === dyn.module) : undefined;
                      const label    = def?.label   ?? custom?.label ?? dyn?.label ?? '';
                      const icon     = def?.icon    ?? (custom ? (DATA_SOURCES.find(d => d.value === custom.dataSource)?.icon ?? '📊') : (dynMod?.icon ?? '📊'));
                      const desc     = def?.desc    ?? (custom ? `${custom.dataSource} · ${custom.chartType}`
                        : (dynMod ? `${dynMod.label} · by ${dynMod.dimensions.find(d2 => d2.field === dyn!.dimension)?.label || dyn!.dimension}` : ''));
                      const defaultW = def?.defaultW ?? custom?.width ?? dyn?.width ?? 6;
                      const onRemove = custom ? () => onRemoveCustom(id) : dyn ? () => onRemoveDynamic(id) : undefined;
                      return (
                        <Draggable key={id} draggableId={id} index={idx}>
                          {(drag, snap) => (
                            <div ref={drag.innerRef} {...drag.draggableProps}>
                              {renderWidgetRow(id, label, icon, desc, defaultW, idx, snap.isDragging, drag.dragHandleProps, onRemove)}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {/* Hidden widgets — Add back */}
          {hiddenIds.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Hidden Widgets</div>
              {hiddenIds.map(id => {
                const def = applicable.find(w => w.id === id);
                const custom = customCharts.find(c => c.id === id);
                const dyn = dynamicCharts.find(d => d.id === id);
                if (!def && !custom && !dyn) return null;
                const dynMod = dyn ? CHART_REGISTRY.find(m => m.id === dyn.module) : undefined;
                const icon  = def?.icon  ?? (custom ? (DATA_SOURCES.find(d => d.value === custom.dataSource)?.icon ?? '📊') : (dynMod?.icon ?? '📊'));
                const label = def?.label ?? custom?.label ?? dyn?.label ?? '';
                return (
                  <button key={id} style={styles.addBtn} onClick={() => onToggleHide(id)}>
                    <span>{icon}</span>
                    <span>+ Show "{label}"</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Custom chart builder */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Create Custom Chart</div>
            {!showBuilder ? (
              <button style={styles.addBtn} onClick={() => setShowBuilder(true)}>
                <span>✨</span><span>+ Add New Chart</span>
              </button>
            ) : (
              <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, border: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Chart Settings</div>

                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 3 }}>Chart Name</div>
                <input
                  style={styles.input}
                  placeholder="e.g. Workload Overview"
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                />

                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 3 }}>Data Source</div>
                <select style={styles.select} value={newSource} onChange={e => setNewSource(e.target.value as CustomDataSource)}>
                  {DATA_SOURCES.map(d => (
                    <option key={d.value} value={d.value}>{d.icon} {d.label}</option>
                  ))}
                </select>

                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 3 }}>Chart Type</div>
                <select style={styles.select} value={newType} onChange={e => setNewType(e.target.value as CustomChartType)}>
                  {CHART_TYPES.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.icon} {ct.label}</option>
                  ))}
                </select>

                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6 }}>Width</div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
                  {WIDTH_OPTIONS.map(o => (
                    <button key={o.value} style={widthBtnStyle(newWidth === o.value)} onClick={() => setNewWidth(o.value)}>
                      {o.label}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...styles.createBtn, flex: 1 }} onClick={handleAddCustom} disabled={!newLabel.trim()}>
                    + Add Chart
                  </button>
                  <button onClick={() => setShowBuilder(false)}
                    style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, background: '#F3F4F6', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Dynamic charts — real API-driven charts */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Dynamic Charts (Live Data)</div>

            {/* Existing dynamic charts */}
            {dynamicCharts.map(dc => {
              const mod = CHART_REGISTRY.find(m => m.id === dc.module);
              const activeFilters = dc.filters.filter(f => f.value);
              return (
                <div key={dc.id} style={{...styles.widgetRow, flexDirection:'column', gap:6}}>
                  <div style={{display:'flex', alignItems:'center', gap:8, width:'100%'}}>
                    <span style={{fontSize:16}}>{mod?.icon ?? '📊'}</span>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={styles.widgetLabel}>{dc.label}</div>
                      <div style={styles.widgetDesc}>
                        {mod?.label} · by {mod?.dimensions.find(d => d.field === dc.dimension)?.label || dc.dimension}
                        {activeFilters.length > 0 && (
                          <span style={{marginLeft:4, color:'#3B82F6'}}>· {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <button style={toggleBtnStyle(hiddenWidgets.has(dc.id))} onClick={() => onToggleHide(dc.id)}>
                        {hiddenWidgets.has(dc.id) ? '+ Show' : '✓ Visible'}
                      </button>
                      <button style={styles.removeBtn} title="Remove" onClick={() => onRemoveDynamic(dc.id)}>✕</button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add dynamic chart button */}
            <button style={{...styles.addBtn, background:'#EFF6FF', borderColor:'#3B82F6', color:'#3B82F6'}}
              onClick={onOpenDynamicBuilder}>
              <span>✨</span><span>+ Create Dynamic Chart</span>
            </button>
          </div>

          {/* Reset */}
          <div style={{ padding: '12px 16px', marginTop: 'auto' }}>
            {confirmReset ? (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 12, color: '#DC2626', marginBottom: 8 }}>Reset all widget positions, widths, and visibility to defaults?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { onReset(); setConfirmReset(false); }}
                    style={{ flex: 1, padding: '6px 12px', borderRadius: 6, background: '#DC2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    Yes, Reset
                  </button>
                  <button onClick={() => setConfirmReset(false)}
                    style={{ flex: 1, padding: '6px 12px', borderRadius: 6, background: '#F3F4F6', color: '#6B7280', border: 'none', cursor: 'pointer', fontSize: 12 }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmReset(true)}
                style={{ width: '100%', padding: '7px', borderRadius: 7, fontSize: 12, color: '#9CA3AF', background: '#F9FAFB', border: '1px solid #E5E7EB', cursor: 'pointer' }}>
                Reset to Defaults
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
